import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Container,
} from '@mui/material';
import Page from '../components/Page';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import { UserListHead } from '../sections/@dashboard/user';

const TABLE_HEAD = [
  { id: 'title', label: 'Tiêu đề', alignRight: false },
  { id: 'user', label: 'Người nhận', alignRight: false },
  { id: 'content', label: 'Nội dung', alignRight: false },
  { id: 'isRead', label: 'Trạng thái', alignRight: false },
  { id: 'createdAt', label: 'Ngày gửi', alignRight: false },
  { id: 'action', label: '', alignRight: true },
];

const EMPTY_FORM = {
  user: '',
  title: '',
  description: '',
  content: '',
};

const token = JSON.parse(localStorage.getItem('user'))?.token;
const baseURL = process.env.REACT_APP_SERVICE_BASE_URL;
const headers = { token };

const getErrorMessage = error => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  return message || 'Có lỗi xảy ra. Vui lòng thử lại.';
};

const PUSH_ERROR_LABELS = {
  'messaging/third-party-auth-error': 'APNs chưa được xác thực trong Firebase',
  'messaging/registration-token-not-registered': 'token thiết bị không còn hiệu lực',
  'messaging/invalid-registration-token': 'token thiết bị không hợp lệ',
  'app/invalid-credential': 'Firebase Admin credential không hợp lệ',
};

const formatPushErrors = errors =>
  (errors || []).map(error => `${PUSH_ERROR_LABELS[error.code] || error.code}: ${error.count}`).join('; ');

export default function NotificationPage() {
  const [notifications, setNotifications] = useState([]);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [snack, setSnack] = useState({ open: false, severity: 'success', message: '' });

  const loadNotifications = useCallback(() => {
    setLoading(true);
    axios
      .get(`${baseURL}/notification?page=${page + 1}&pageSize=${rowsPerPage}`, { headers })
      .then(response => {
        const { data } = response.data;
        setNotifications(data.records || []);
        setTotal(data.total || 0);
      })
      .catch(error => {
        setSnack({ open: true, severity: 'error', message: getErrorMessage(error) });
      })
      .finally(() => setLoading(false));
  }, [page, rowsPerPage]);

  const loadUsers = useCallback(() => {
    axios
      .post(`${baseURL}/admin/users/list?page=1&pageSize=0`, null, { headers })
      .then(response => setUsers(response.data.data.records || []))
      .catch(error => {
        setSnack({ open: true, severity: 'error', message: getErrorMessage(error) });
      });
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const resetDialog = () => {
    setOpen(false);
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const closeDialog = () => {
    if (submitting) return;
    resetDialog();
  };

  const submit = () => {
    if (!form.user || !form.title.trim() || !form.description.trim() || !form.content.trim()) {
      setFormError('Vui lòng nhập đầy đủ người nhận, tiêu đề, mô tả và nội dung.');
      return;
    }

    setSubmitting(true);
    setFormError('');
    axios
      .post(
        `${baseURL}/notification/create`,
        {
          user: form.user,
          title: form.title.trim(),
          description: form.description.trim(),
          content: form.content.trim(),
          isActivated: true,
        },
        { headers },
      )
      .then(response => {
        const push = response.data.data?.push || {};
        const pushErrors = formatPushErrors(push.errors);
        setSnack({
          open: true,
          severity: push.failureCount > 0 ? 'warning' : 'success',
          message: `Đã tạo thông báo. Push thành công: ${push.successCount || 0}, thất bại: ${push.failureCount || 0}.${
            pushErrors ? ` Chi tiết: ${pushErrors}.` : ''
          }`,
        });
        resetDialog();
        if (page === 0) loadNotifications();
        else setPage(0);
      })
      .catch(error => setFormError(getErrorMessage(error)))
      .finally(() => setSubmitting(false));
  };

  const remove = notification => {
    if (!window.confirm(`Xóa thông báo “${notification.title || ''}”?`)) return;
    axios
      .delete(`${baseURL}/notification/${notification.id}`, { headers })
      .then(() => {
        setSnack({ open: true, severity: 'success', message: 'Đã xóa thông báo.' });
        loadNotifications();
      })
      .catch(error => {
        setSnack({ open: true, severity: 'error', message: getErrorMessage(error) });
      });
  };

  const selectedUser = users.find(user => user._id === form.user) || null;

  return (
    <Page title="Quản lý thông báo">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4">Quản lý thông báo</Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setOpen(true)}>
            Gửi thông báo
          </Button>
        </Stack>

        <Card>
          {loading ? <LinearProgress /> : null}
          <Scrollbar>
            <TableContainer sx={{ minWidth: 900 }}>
              <Table>
                <UserListHead headLabel={TABLE_HEAD} />
                <TableBody>
                  {notifications.length > 0 ? (
                    notifications.map(notification => (
                      <TableRow hover key={notification.id}>
                        <TableCell>{notification.title}</TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">{notification.user?.name || 'Người dùng đã xóa'}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {notification.user?.email || notification.user?.phone || ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 320 }}>{notification.content}</TableCell>
                        <TableCell>{notification.isRead ? 'Đã đọc' : 'Chưa đọc'}</TableCell>
                        <TableCell>
                          {notification.createdAt ? new Date(notification.createdAt).toLocaleString('vi-VN') : ''}
                        </TableCell>
                        <TableCell align="right">
                          <Button color="error" size="small" onClick={() => remove(notification)}>
                            Xóa
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        Chưa có thông báo.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, value) => setPage(value)}
            onRowsPerPageChange={event => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </Card>
      </Container>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>Gửi thông báo cho người dùng</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {formError ? <Alert severity="error">{formError}</Alert> : null}
            <Autocomplete
              options={users}
              value={selectedUser}
              getOptionLabel={option => `${option.name || 'Không tên'} — ${option.email || option.phone || option._id}`}
              onChange={(event, value) => setForm(current => ({ ...current, user: value?._id || '' }))}
              renderInput={params => <TextField {...params} label="Người nhận" required />}
            />
            <TextField
              label="Tiêu đề"
              required
              value={form.title}
              onChange={event => setForm(current => ({ ...current, title: event.target.value }))}
            />
            <TextField
              label="Mô tả"
              required
              value={form.description}
              onChange={event => setForm(current => ({ ...current, description: event.target.value }))}
            />
            <TextField
              label="Nội dung push"
              required
              multiline
              minRows={4}
              value={form.content}
              onChange={event => setForm(current => ({ ...current, content: event.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Hủy
          </Button>
          <Button variant="contained" onClick={submit} disabled={submitting}>
            {submitting ? 'Đang gửi…' : 'Gửi thông báo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack(current => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack(current => ({ ...current, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Page>
  );
}
